/**
 * Story 8.12 — Onglet Équipe en lecture seule (modale profil membre)
 * Même layout que TabEquipe du Profil, mais sans actions.
 * DA Centralis : bannières #595959, tableau lisible.
 * Supporte les rôles AC (Gestionnaire, Signataire, Interlocuteur) et libre (Administrateur, Membre).
 */
import React from 'react';
import type { ProfileData } from '../../../../../services/profileService';
import { convertApiRoleToUiRole } from '../../../../../utils/roleUtils';
import SectionPliable from '../../../../../composants/SectionPliable/SectionPliable';
import AffectationApercu from '../../../../../composants/AffectationCarte/AffectationApercu';
import '../../../../../composants/GestionComptesPrestataires/GestionComptesPrestataires.css';

interface TeamMember {
    id: number | string;
    nom: string;
    prenom: string;
    email: string;
    telephone: string;
    fonction: string;
    role: string;
    is_admin: boolean;
}

interface ProfileDataWithTeam extends ProfileData {
    team?: TeamMember[];
}

interface ReadOnlyEquipeProps {
    data: ProfileData;
}

const formatPhone = (phone?: string | null): string => {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    let n = digits;
    if (digits.startsWith('33') && digits.length === 11) n = digits.slice(2);
    else if (digits.startsWith('0') && digits.length === 10) n = digits.slice(1);
    else return phone;
    return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
};

const formatEmail = (email?: string | null): string => email ? email.toLowerCase() : '-';

const ReadOnlyEquipe: React.FC<ReadOnlyEquipeProps> = ({ data }) => {
    const team = (data as ProfileDataWithTeam).team ?? [];
    const lotContacts = data.lot_contacts ?? [];

    return (
        <div className="readonly-tab readonly-equipe">
            <SectionPliable title="Membres de l'équipe" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    {team.length === 0 ? (
                        <p className="readonly-empty">Aucun membre dans l'équipe.</p>
                    ) : (
                        <div className="membres-cards-grid">
                            {team.map((member) => {
                                const initials = [member.prenom?.[0], member.nom?.[0]].filter(Boolean).join('').toUpperCase() || '?';
                                const lower = member.role.toLowerCase();
                                const uiRole = lower === 'membre' ? 'Prestataire libre'
                                    : lower === 'administrateur' ? 'Prestataire libre admin'
                                        : convertApiRoleToUiRole(lower);
                                return (
                                    <div key={member.id} className={`membre-card${uiRole.toLowerCase().includes('signataire') ? ' membre-card--titulaire' : ' membre-card--libre'}`}>
                                        <div className="membre-card-inner">
                                            <span className="membre-card-role-badge">{uiRole}</span>
                                            <div className="membre-card-header">
                                                <div className="membre-card-banner" />
                                                <div className="membre-card-avatar-wrapper">
                                                    <span className="membre-card-avatar">{initials}</span>
                                                </div>
                                            </div>
                                            <div className="membre-card-body">
                                                <p className="membre-card-name">
                                                    {member.prenom ? member.prenom.charAt(0).toUpperCase() + member.prenom.slice(1).toLowerCase() : ''} {member.nom.toUpperCase()}
                                                </p>
                                                {member.fonction && <p className="membre-card-fonction">{member.fonction.charAt(0).toUpperCase() + member.fonction.slice(1).toLowerCase()}</p>}
                                                <div className="membre-card-contacts">
                                                    {member.email && <span className="membre-card-contact membre-card-email">{formatEmail(member.email)}</span>}
                                                    {member.telephone && <span className="membre-card-contact">{formatPhone(member.telephone)}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SectionPliable>

            {/* Affectation des membres sur les lots — apercu carte / liste (lecture seule). */}
            <SectionPliable title="Affectation des membres sur les lots" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <AffectationApercu lotContacts={lotContacts} />
                </div>
            </SectionPliable>
        </div>
    );
};

export default ReadOnlyEquipe;
